import { useState, useCallback, useRef } from 'react';
import { DocumentNode } from 'graphql';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import {
  useCreateSchedulerOpenShiftAssignmentRequestMutation,
  useDeleteSchedulerOpenShiftAssignmentRequestMutation,
} from '../../../__generated__/graphql';

type PendingAction = {
  openShiftId: string;
  type: 'requesting' | 'canceling';
} | null;

type UseOpenShiftActionsResult = {
  requestShift: (openShiftId: string) => void;
  cancelRequest: (openShiftId: string, assignmentRequestId: string) => void;
  pendingAction: PendingAction;
};

export const useOpenShiftActions = (
  refetchDocument: DocumentNode,
  onComplete?: () => void,
): UseOpenShiftActionsResult => {
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const [createAssignmentRequest] =
    useCreateSchedulerOpenShiftAssignmentRequestMutation({
      awaitRefetchQueries: true,
      /* eslint-disable-next-line no-restricted-syntax */
      refetchQueries: [refetchDocument],
    });
  const [deleteAssignmentRequest] =
    useDeleteSchedulerOpenShiftAssignmentRequestMutation({
      awaitRefetchQueries: true,
      /* eslint-disable-next-line no-restricted-syntax */
      refetchQueries: [refetchDocument],
    });

  const errorHandler = useMutationErrorHandler();

  const requestShift = useCallback(
    (openShiftId: string) => {
      setPendingAction({ openShiftId, type: 'requesting' });
      createAssignmentRequest({
        variables: { input: { openShiftId } },
      })
        .then(() => {
          onCompleteRef.current?.();
        })
        .catch(errorHandler)
        .finally(() => {
          setPendingAction(null);
        });
    },
    [createAssignmentRequest, errorHandler],
  );

  const cancelRequest = useCallback(
    (openShiftId: string, assignmentRequestId: string) => {
      setPendingAction({ openShiftId, type: 'canceling' });
      deleteAssignmentRequest({
        variables: { id: assignmentRequestId },
      })
        .then(() => {
          onCompleteRef.current?.();
        })
        .catch(errorHandler)
        .finally(() => {
          setPendingAction(null);
        });
    },
    [deleteAssignmentRequest, errorHandler],
  );

  return { requestShift, cancelRequest, pendingAction };
};
